import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import classNames from 'classnames';
import qs from 'qs';
import useGtm from 'Douleutaras/hooks/gtm';
import DefaultFullLayout from 'Douleutaras/layout/DefaultFullLayout';
import {
  fetchSeller, selectAvailableAutodealCategories, sellerProfilesSelectors
} from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { getSellerReviews } from 'Douleutaras/redux/data/seller/sellerReviewsSlice';
import { sellerProfilesMunicipalitiesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesMunicipalitiesSlice';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { IntroSection } from 'Douleutaras/components/IntroSection/IntroSection';
import ErrorScreen from 'Douleutaras/layout/service-pages/ErrorScreen';
import Tabs from 'Douleutaras/components/Tabs/Tabs';
import Tab from 'Douleutaras/components/Tabs/Tab';
import { Trans, useTranslation } from 'react-i18next';
import LineClamp from 'Douleutaras/components/LineClamp/LineClamp';
import SkeletonLoader from 'Douleutaras/components/Loaders/SkeletonLoader';
import isEqual from 'lodash/isEqual';
import { getModalsSlugs, modalSelectors } from 'Douleutaras/redux/data/modals/modalsSlice';
import { DashboardJobViewRouteParams } from 'Douleutaras/views/client-dashboard/components/DashboardJobView';
import Modal from 'Douleutaras/components/Modal/Modal';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import SocialShare from 'Douleutaras/components/SocialShare/SocialShare';
import shareIcon from 'Assets/layout/socialIcons/share.svg';
import { useMediaQuery } from 'react-responsive';
import loadable from '@loadable/component';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import { SubscriptionTierPro } from 'Douleutaras/types';
import goldRing from 'Assets/layout/profile/gold-ring.svg';
import topProBadge from 'Assets/layout/profile/top-pro-badge.svg';
import topProImgMobi from 'Assets/layout/profile/PH_Top_Mobile.jpg';
import topProImgDesk from 'Assets/layout/profile/PH_Top_Desktop.jpg';
import proImgMobi from 'Assets/layout/profile/PH_P_Mobile.jpg';
import proImgDesk from 'Assets/layout/profile/PH_P_Desktop.jpg';
import { PaymentMethods } from 'Douleutaras/views/client-dashboard/components/PaymentMethods/PaymentMethods';
import { buildUrl, VALID_URLS } from 'Douleutaras/utilities/url-builder';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { useStickyCtaOffset } from 'Douleutaras/hooks/useStickyCtaOffset';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import {
  ALLOWED_ANALYTICS_REF_GET_PARAMS, ProfileDetailsParams, PROFILE_FETCH_COMMON_GET_PARAMS, PROFILE_FETCH_COMMON_INCLUDES
} from 'Douleutaras/constants/pro-profile';
import { useFlowParam } from 'Douleutaras/hooks/jobs/jobFlow';
import { PostHogEventNames } from 'Douleutaras/app/lib/posthog/posthog.provider';
import { useAB } from 'Douleutaras/app/lib/ab';
import { ExtraBidModal } from 'Douleutaras/components/modals/ExtraBidModal';
import { getSellerServicePrices } from 'Douleutaras/redux/data/seller/servicesPricingSlice';
import { GetServerSideProps } from 'Douleutaras/types/ssr';
import ServicesSection from 'Douleutaras/views/pro-profile/components/ServicesSection';

import { getServerSideProps as reviewSectionSSP, ReviewSection } from './ReviewSection';
import { MenuScrollableWrapper } from './MenuScrollableWrapper';
import { PageEventNames } from './settings/gtm';
import { ProProfileSchema } from './ProProfileSchema';
import ServicesCatalogue, { deriveServicePriceFlags } from './ServicesCatalogue/ServicesCatalogue';
import { useSellerServicePrices } from './hooks/useSellerServicePrices';
import { useServicesCatalogueHandlers } from './hooks/useServicesCatalogueHandlers';
import ProBadges from './ProBadges';
import ProAboutTextLoader from './loaders/ProAboutTextLoader';
import ProCleanerAttributes from './ProCleanerAttributes';
import ProProfilePortfolioSection from './ProProfilePortfolioSection';
import { CtaFloatingSection } from './CtaFloatingSectionComponents/CtaFloatingSection';
import Categories from './Categories';
import './ProProfile.scss';

// Lazy: only needed behind the nested /post-job route (most visitors never
// hit it), and it pulls in @react-google-maps/api transitively via LocationStep.
const PostJobModal = loadable(() => import('../../../functional-components/post-job/PostJobModal'));

export const getServerSideProps: GetServerSideProps = async ({ store, params }) => {
  if (!params.username) { return; }
  // The catalogue read is public and seller-scoped by username, so it needs nothing from the
  // profile fetch — it runs in parallel with it (same prices for every visitor of a given pro,
  // safe to prime server-side). A doomed request for a non-existent username just yields an empty
  // catalogue, which the section/tab already hides on.
  await Promise.all([
    store.dispatch(fetchSeller({
      username: params.username,
      includes: PROFILE_FETCH_COMMON_INCLUDES,
      additionalGetParams: PROFILE_FETCH_COMMON_GET_PARAMS
    })),
    reviewSectionSSP({ store, params }),
    store.dispatch(getSellerServicePrices({ username: params.username }))
  ]);
};

// The hero shows a one-decimal rating ("4,7"), unlike the two-decimal average the API returns.
const RATING_DECIMALS = 1;

const PROFILE_NOT_FOUND_CODE = 404;
const PROFILE_INACTIVE_CORE = 404;

// Read by support-widget.global.scss to lift the launcher above the mobile CTA bar
const PRO_PROFILE_STICKY_CTA_CLASS = 'has-pro-profile-sticky-cta';

interface IntroSectionProps {
  gird: string;
  gap?: string;
  imageMobile?: string;
  image?: string;
  backButton?: boolean;
  backShadow?: boolean;
}

export const ProProfile = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  // tslint:disable-next-line:no-null-keyword
  const parentRef = useRef<HTMLDivElement>(null);
  const { track } = useGtm();
  const { formatter, countryCode } = useLocalization();

  const location = useLocation();
  // Multi bid variables
  const params = qs.parse(location.search?.slice(1));
  const { username } = useParams<DashboardJobViewRouteParams | ProfileDetailsParams>();
  const [activeTab, setActiveTab] = useState('about');
  // Real height of the pinned tab menu, fed back by MenuScrollableWrapper and published as a CSS
  // variable so each section's `scroll-margin-top` clears both the site header and the menu.
  const [menuHeight, setMenuHeight] = useState(0);
  const ab = useAB();
  const postingFlowResponse = useAppSelector(state => state.pros.postingFlow);
  const { servicesPricing, areServicePriceFlagsReady, showServicesCatalogue } = useSellerServicePrices(username);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const rawUser = useAppSelector(selectCurrentUser);
  const user = mounted ? rawUser : undefined;
  const { loading, error } = useAppSelector(state => state.sellerProfiles);
  const profile = useAppSelector(state => sellerProfilesSelectors.selectByUsername(state, username));
  const municipalityIds = profile?.relationships.municipalities?.data.map(m => m.id) ?? [];
  const municipalities = useAppSelector(state =>
    municipalityIds.map(id => sellerProfilesMunicipalitiesSelectors.selectById(state, id))
      .filter(Boolean)
  );
  const modals = useAppSelector(modalSelectors.selectEntities);
  // Gates the related-services heading on the same filtered list the section renders
  const relatedCategories = useAppSelector(state => selectAvailableAutodealCategories(state, username));

  const [ showShare, setShowShare ] = useState(false);
  const [ showExtraBid, setShowExtraBid ] = useState(false);
  const [ willRedirectToHomepage, setWillRedirectToHomepage ] = useState(false);
  const [ portfolioIsOpenFromOutside, setPortfolioIsOpenFromOutside ] = useState(-1);

  const [isStickyBackHidden, setIsStickyBackHidden] = useState(true);
  const [isStickyBackDownHidden, setIsStickyBackDownHidden] = useState(true);
  const lastScrollY = useRef(0);

  // Create navigation to a parent path with or without search params
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const flow = useFlowParam();
  const proListFlow = flow === POST_JOB_FLOW.EXTRA_BID;
  const autoDealId = location.state?.autoDealId;

  const navigateToInitialLocation = () => {
    navigate(`.${ searchParamsString ? `?${searchParamsString}` : ''}`, {
      relative: 'path',
      state: { flow, autoDealId },
      replace: true
    });
  };

  const {
    handleServiceClick,
    handleCategoryExpand,
    handleCategoryInfoClick,
    handleServiceInfoClick
  } = useServicesCatalogueHandlers({ flow, autoDealId });

  const ctaBarRef = useStickyCtaOffset(PRO_PROFILE_STICKY_CTA_CLASS);

  const isMobile = useMediaQuery(
    { maxWidth: TABLET_BIG.MIN_WIDTH - 1 }
  );

  // Scrolls the section into view under the fixed header. The gap is `scroll-margin-top` on the
  // section itself (see `.tabs-content article` in ProProfile.scss) rather than an offset computed
  // here, so the browser resolves it against the final layout — a number measured at click time
  // goes stale as soon as an image loads or the tab menu pins itself.
  const scrollToTab = (tabId: string) => {
    setActiveTab(tabId);

    document.getElementById(tabId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // @GA_EVENT
    track(PageEventNames.BADGE_CLICKED, undefined, tabId);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [username]);

  useEffect(() => {
    // Do not fetch if profile is already loaded
    if (Boolean(profile) && username === profile?.attributes?.username) {
      return;
    }

    // Make the API call
    if (username) {
      dispatch(fetchSeller(({ username,
        includes: PROFILE_FETCH_COMMON_INCLUDES,
        additionalGetParams: PROFILE_FETCH_COMMON_GET_PARAMS
      })))
          .then(res => {
            // @ts-ignore
            if (res?.payload?.response?.status === PROFILE_INACTIVE_CORE || res?.payload?.response?.status === PROFILE_NOT_FOUND_CODE) {
              setWillRedirectToHomepage(true);
            }
          });
    }
    if (isEqual(modals, {})) {
      dispatch(getModalsSlugs({
        'fields[infoModal]': 'id,slug'
      }));
    }
    // @GA_EVENT
    track(PageEventNames.PAGE_VIEWED);
  }, [ location.pathname, username ]);

  useEffect(() => {
    if (!profile?.id || !areServicePriceFlagsReady) {
      return;
    }

    // Check if there's referral in page's url like ?ref=chat
    const ref = params?.ref && ALLOWED_ANALYTICS_REF_GET_PARAMS.includes(params.ref.toString())
      ? params.ref
      : undefined;

    // Both flags move to the profile payload once #5614 lands.
    const { hasServices, hasServicePrices } = deriveServicePriceFlags(servicesPricing.data);

    const captureProfileViewed = (hasPinnedReview: boolean) => {
      // @ab PostHog event
      ab.capture(PostHogEventNames.PRO_PROFILE_VIEWED, {
        category_slug: postingFlowResponse.category_slug,
        sub_category_slug: postingFlowResponse.sub_category_slug,
        source: ref,
        sort_order: 'default',
        position: location?.state?.proPosition,
        pro_id: profile.id,
        has_pinned_review: hasPinnedReview,
        has_services: hasServices,
        has_service_prices: hasServicePrices
      });
    };

    // Resolve the pinned-review state explicitly so the event always carries an
    // accurate boolean (never null/omitted), independent of ReviewSection's fetch timing.
    dispatch(getSellerReviews({ username, page: 1, isPinned: true }))
      .unwrap()
      .then(payload => captureProfileViewed(Boolean(payload?.pinnedId)))
      .catch(() => captureProfileViewed(false));
  }, [profile?.id, areServicePriceFlagsReady]);

  useEffect(() => {
    if (!proListFlow) {
      setIsStickyBackHidden(true);
      setIsStickyBackDownHidden(true);

      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;

      if (currentScrollY <= 55) {
        // Always hidden at the top of the page
        setIsStickyBackHidden(true);
        setIsStickyBackDownHidden(true);
      } else {
        // Always visible after 150px threshold
        setIsStickyBackHidden(false);

        if (isMobile) {
          if (Math.abs(delta) > 10) {
            const scrollingUp = delta < 0;
            // When scrolling up: slide button down to reveal main menu
            // When scrolling down: move button up to cover main menu
            setIsStickyBackDownHidden(!scrollingUp);
          }
        } else {
          // Desktop: keep in default top position
          setIsStickyBackDownHidden(true);
        }
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sellerDisplayName = profile?.attributes?.sellerDisplayName;
  const isMyProfile = user?.internal_username === username;

  const servicesCatalogue = servicesPricing.data;

  const metaDescription = useMemo(() => {
    if (!profile) {
      return undefined;
    }
    const siteName = COUNTRY_CONFIG.SITE_NAME[countryCode];
    const area = municipalities[0]?.attributes.title;
    const hasArea = Boolean(area);
    const hasReviews = profile.attributes.sellerFeedbacks > 0;
    const interpolation = {
      name: profile.attributes.sellerDisplayName,
      category: profile.attributes.favoriteCategory,
      desc: (profile.attributes.sellerOverviewPublic ?? '').replace(/\s+/g, ' ')
        .trim(),
      area,
      rating: formatter.number(profile.attributes.sellerRating),
      reviewCount: profile.attributes.sellerFeedbacks,
      siteName
    };

    let translated: string;
    switch (true) {
      // Has both Area and Reviews
      case hasArea && hasReviews:
        translated = t('T.PROS-PROFILE.META.DESCRIPTION-FULL-{{name}}-{{category}}-{{desc}}-{{area}}-{{rating}}-{{reviewCount}}-{{siteName}}'
          , interpolation);
        break;
      // Has Area but no reviews
      case hasArea:
        translated = t('T.PROS-PROFILE.META.DESCRIPTION-AREA-{{name}}-{{category}}-{{desc}}-{{area}}-{{siteName}}'
          , interpolation);
        break;
      // Has Reviews but no Area
      case hasReviews:
        translated = t('T.PROS-PROFILE.META.DESCRIPTION-REVIEWS-{{name}}-{{category}}-{{desc}}-{{rating}}-{{reviewCount}}-{{siteName}}'
          , interpolation);
        break;
      // Has no Area nor reviews
      default:
        translated = t('T.PROS-PROFILE.META.DESCRIPTION-{{name}}-{{category}}-{{desc}}-{{siteName}}', interpolation);
    }
    if (translated.startsWith('T.PROS-PROFILE.META.DESCRIPTION')) {
      const raw = profile.attributes.sellerOverviewPublic;

      return raw ?
        raw.replace(/\s+/g, ' ')
          .trim() || undefined
        : undefined;
    }
    if (translated.length > 160) {
      const cut = translated.slice(0, 157);
      const lastSpace = cut.lastIndexOf(' ');

      return `${cut.slice(0, lastSpace > 0 ? lastSpace : 157)}…`;
    }

    return translated;
  }, [profile, municipalities, countryCode, formatter]);

  // Redirects to homepage under certain conditions
  if (willRedirectToHomepage) {
    window.location.href = '/';

    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  if (error && !willRedirectToHomepage && !loading) {
    return <ErrorScreen />;
  }

  const shareUrl = `${window.location.origin}${profile?.attributes.publicUrl}`;
  const shareTitle = t('T.SHARE.Share Profile');
  const shareText = t('T.SHARE.Book on Douleutaras', {sellerDisplayName});

  const proTier = profile?.attributes?.subscriptionTier;
  const isGoldOrSilverTier = proTier === SubscriptionTierPro.GOLD || proTier === SubscriptionTierPro.SILVER;
  const isGoldTier = proTier === SubscriptionTierPro.GOLD;

  const hasDesktopImage = Boolean(profile?.attributes?.desktopBackgroundImage);
  const hasMobileImage = Boolean(profile?.attributes?.mobileBackgroundImage);

  const image = hasDesktopImage
    ? profile.attributes.desktopBackgroundImage
    : (isGoldTier ? topProImgDesk : proImgDesk);

  const imageMobile = hasMobileImage
    ? profile.attributes.mobileBackgroundImage
    : (isGoldTier ? topProImgMobi : proImgMobi);

  const introProps: IntroSectionProps = isGoldOrSilverTier
    ? { gird: 'xs-4 sm-8 lg-12 dt-10 ext-10', gap: '', imageMobile, image, backShadow: proListFlow}
    : { gird: 'xs-4 sm-6 lg-9 dt-8 ext-8', gap: 'sm-l-2 lg-l-2 dt-l-2', backButton: proListFlow};

  const handleShareClick = async () => {
    // @GA_EVENT
    track(PageEventNames.SHARE_CLICKED);

    // Check if Web Share API is available (usually on mobile)
    if (navigator.share && isMobile) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
    } else {
      // Fallback to desktop behavior (show existing share modal)
      setShowShare(true);
    }
  };

  const backButtonClick = () => {
    navigate(-1);
  };

  const handleCloseModal = () => {
    setShowShare(false);
  };

  const proProfileImageClick = () => {
    // @GA_EVENT
    track(PageEventNames.AVATAR_CLICKED);
    setPortfolioIsOpenFromOutside(0);
  };

  return (
    <DefaultFullLayout proHeader={isMyProfile}>
      <Helmet>
        <title>
          {sellerDisplayName}
        </title>
        {profile && (
          <link rel="canonical" href={`${window.location.origin}${buildUrl(VALID_URLS.PROFILE, profile.attributes.username)}/`} />
        )}
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {profile && [
          <meta key="og-type" property="og:type" content="profile" />,
          <meta key="og-url" property="og:url" content={`${window.location.origin}${buildUrl(VALID_URLS.PROFILE, profile.attributes.username)}/`} />,
          <meta key="og-title" property="og:title" content={sellerDisplayName} />,
          Boolean(metaDescription) && <meta key="og-description" property="og:description" content={metaDescription} />,
          Boolean(profile.attributes.mugshot) && <meta key="og-image" property="og:image" content={profile.attributes.mugshot} />,
          <meta key="twitter-card" name="twitter:card" content="summary_large_image" />
        ]}
        {
          !Boolean(profile?.attributes.isSeoEligible) && <meta name="robots" content="noindex" />
        }
      </Helmet>
      <ProProfileSchema username={username} />

      { proListFlow  &&
        <div styleName={classNames('sticky-header', {
          'sticky-header--visible': !isStickyBackHidden,
          'sticky-header--down': !isStickyBackDownHidden
        })} data-grid="container-fluid" >
          <div data-grid-size={introProps.gird}>
            <div styleName="sticky-header__inner" onClick={backButtonClick}>
              <span styleName="sticky-header__icon" />
              <span styleName="sticky-header__text">{t('T.PROS-PROFILE.Back')}</span>
            </div>
          </div>
        </div>
      }

      { Boolean(profile?.id) &&
        <Routes>
          <Route path="post-job" element={
            <PostJobModal
              closeButtonEnabled={true}
              autoDealIds={[ autoDealId ]}
              closeButtonCallback={navigateToInitialLocation}
              proId={profile.id}
              proUsername={profile?.attributes.username}
              proMugshot={profile?.attributes.mugshot}
              showProAvatar
              internalFlow={flow}
            />
          } />
        </Routes>
      }
      <div styleName="pro-profile-page">
      <IntroSection {...introProps} loading={loading} >
          <div styleName={classNames('intro', {
            'intro--background': isGoldOrSilverTier,
            'intro--partner': isGoldOrSilverTier,
            'intro--gold': isGoldTier,
            'intro--loading': loading
          })}>

            {loading
              ? <></>
              : <div styleName="intro__image" onClick={() => proProfileImageClick()}>
                <img styleName="intro__image-main"
                       src={profile?.attributes.mugshot}
                       alt={profile?.attributes.username}
                       fetchpriority="high" />
                {isGoldTier &&
                  <>
                    <img styleName="intro__image-golden-ring" src={goldRing} alt="TopProRing" />
                    <img styleName="intro__badge" src={topProBadge} alt="TopProBadge" />
                  </>
                }
              </div>
            }

            <h1 styleName="intro__title">
              {loading
                ? <SkeletonLoader type="line" width={280} height={32} />
                : <>{sellerDisplayName}</>
              }
            </h1>
            { proListFlow &&
              <div onClick={backButtonClick} styleName={classNames('back-button', {
                'back-button--pro': isGoldOrSilverTier
              })}>
                <span styleName="back-button__icon"/>
                <span styleName="back-button__text">{t('T.PROS-PROFILE.Back')}</span>
              </div>
            }
            <div styleName="intro__share">
              <button styleName="share-button" onClick={handleShareClick} type="button" disabled={loading}>
                <span styleName="share-button__title">{t('T.SHARE.Share profile')}</span>
                <span styleName="share-button__icon-wrap">
                  <img styleName="share-button__icon" src={shareIcon} alt="share" />
                </span>
              </button>
            </div>
            <div styleName="intro__category">
            {loading
                ? <SkeletonLoader type="line" width={180} height={18} />
                : <Categories username={username} primaryCategory={profile?.attributes?.favoriteCategory} />
              }
            </div>
            <div styleName="intro__list">
              {(loading || Number(profile?.attributes.sellerRating) > 0) &&
                <button styleName="intro-item" onClick={() => scrollToTab('reviews')}>
                  <span styleName="intro-item__icon" className="dashicon dashicon-star" />
                  {loading
                    ? <span styleName="intro-item__text"><SkeletonLoader type="line" width={40} height={20} inLine /> &nbsp;
                      <SkeletonLoader type="line" width={90} height={20} inLine />
                      </span>
                    : <span styleName="intro-item__text">
                      {/* tslint:disable-next-line:newline-per-chained-call */}
                      {formatter.number(Number(profile?.attributes.sellerRating).toFixed(RATING_DECIMALS))}
                      {' • '}
                      {profile?.attributes.sellerFeedbacks} {t('T.PROLIST.Reviews')}
                      <span
                        styleName="intro-item__verified"
                        className="dashicon dashicon-checkmark-star"
                        aria-hidden="true"
                      />
                      </span>
                  }
                </button>
              }
              {!profile?.attributes.isCreditModelSeller &&
                (loading || profile?.attributes.concludedJobsCnt > 0) &&
                <div styleName="intro-item">
                  <span styleName="intro-item__icon" className="dashicon dashicon-tools" />
                  <span styleName="intro-item__text">
                  {loading
                    ? <SkeletonLoader type="line" width={25} height={20} inLine />
                    : profile?.attributes.concludedJobsCnt
                  }
                    &nbsp;{t('T.PROS-PROFILE.INTRO.JOBS IN DOULEUTARAS')}
                </span>
                </div>
              }
              <div styleName="intro-item">
                <span styleName="intro-item__icon" className="dashicon dashicon-thank-you"/>
                <span styleName="intro-item__text">
                {t('T.PROS-PROFILE.INTRO.MEMBER SINCE')}&nbsp;
                  {loading ? <SkeletonLoader type="line" width={35} height={20} inLine/> : profile?.attributes.memberSince}
              </span>
              </div>
            </div>
            <div styleName="intro__payment-methods">
              <PaymentMethods methods={profile?.attributes.availablePaymentMethods} large />
            </div>
          </div>
      </IntroSection>

      <main data-grid="container-fluid" ref={parentRef}>
        <div styleName="pro-inner-content" data-grid="row" data-grid-justify="start">
          <div
            data-grid="item"
            data-grid-gap="dt-l-1"
            data-grid-size="xs-4 sm-8 lg-7 dt-6 ext-6"
            style={{ '--pro-profile-menu-height': `${menuHeight}px` } as React.CSSProperties}
          >
            <header styleName="pro-inner-content__header">
              <MenuScrollableWrapper parentRef={parentRef} onHeightChange={setMenuHeight}>
                <Tabs fullWidth onChange={scrollToTab}>
                  <Tab isActive={activeTab === 'about'} tabId="about">{t('T.PROS-PROFILE.TABS.TAB TITLE ABOUT')}</Tab>
                  <Tab isActive={activeTab === 'portfolio'} tabId="portfolio">{t('T.PROS-PROFILE.TABS.TAB TITLE PORTFOLIO')}</Tab>
                  <Tab isActive={activeTab === 'services'} tabId="services"
                       hidden={!showServicesCatalogue}
                  >{t('T.PROS-PROFILE.TABS.TAB TITLE SERVICES')}</Tab>
                  <Tab isActive={activeTab === 'reviews'} tabId="reviews">{t('T.PROS-PROFILE.TABS.TAB TITLE REVIEWS')}</Tab>
                </Tabs>
              </MenuScrollableWrapper>
            </header>
            <section styleName="tabs-content">
              <article id="about">
                <h2 styleName="tabs-content__title">
                  {t('T.PROS-PROFILE.TABS.CONTENT TITLE ABOUT')}
                </h2>
                {profile?.attributes?.cleanerSettings &&
                  <div styleName="tabs-content__sub-header">
                    <ProCleanerAttributes username={username} />
                  </div>
                }
                <div styleName="tabs-content__text">
                  {loading
                    ? <ProAboutTextLoader />
                    : (
                      profile?.attributes.sellerOverviewPublic?.length > 0
                        ? <LineClamp
                          maxChars={260}
                          moreText={t('T.PROS-PROFILE.ABOUT.SHOW-MORE')}
                          lessText={t('T.PROS-PROFILE.ABOUT.SHOW-LESS')}
                          offsetToleranceChars={20}
                        >
                          {profile?.attributes.sellerOverviewPublic}
                        </LineClamp>
                        : <span styleName="tabs-content__text tabs-content__text--empty">{t('T.PROS-PROFILE.ABOUT.NO-INFO-YET')}</span>
                    )
                  }
                </div>
                {profile?.relationships.badges.data.length > 0 &&
                  <div styleName="tabs-content__badges">
                    <ProBadges username={username} />
                  </div>
                }
              </article>

              <article id="portfolio">
                <h2 styleName="tabs-content__title">{t('T.PROS-PROFILE.TABS.CONTENT TITLE PORTFOLIO')}</h2>
                {!loading
                  ? <ProProfilePortfolioSection
                    portfolioIsOpenFromOutside={portfolioIsOpenFromOutside}
                    portfolioDismissCallBack={setPortfolioIsOpenFromOutside}
                    username={username}
                  />
                  : <div>
                    <SkeletonLoader type="line" width={165} height={165} />
                  </div>
                }
              </article>
              {showServicesCatalogue &&
                <article id="services">
                  <h2 styleName="tabs-content__title">{t('T.PROS-PROFILE.TABS.CONTENT TITLE SERVICES')}</h2>
                  <ServicesCatalogue
                    catalogue={servicesCatalogue}
                    loading={servicesPricing.loading}
                    error={servicesPricing.error}
                    onServiceClick={handleServiceClick}
                    onCategoryExpand={handleCategoryExpand}
                    onCategoryInfoClick={handleCategoryInfoClick}
                    onServiceInfoClick={handleServiceInfoClick}
                  />
                </article>
              }
              <article id="reviews">
                <h2 styleName="tabs-content__title">{t('T.PROS-PROFILE.TABS.CONTENT TITLE REVIEWS')}</h2>
                {loading || profile?.attributes.sellerFeedbacks > 0
                  ? <ReviewSection username={username} />
                  : <div styleName="tabs-content__text tabs-content__text--empty">{t('T.PROS-PROFILE.REVIEW.NO REVIEW TEXT')}</div>
                }
              </article>
              {relatedCategories.length > 0 && !profile?.attributes?.cleanerSettings && // not a cleaner
                <article id="related-services">
                  <h2 styleName="tabs-content__title">{t('T.PROS-PROFILE.RELATED-SERVICES.TITLE')}</h2>
                  <ServicesSection username={username} />
                </article>
              }
            </section>
          </div>
          <div styleName="pro-inner-content__aside" ref={ctaBarRef}>
            <CtaFloatingSection isLoggedIn={Boolean(user)}
                                username={username}
                                onClick={() => {
                                  switch (flow) {
                                    case POST_JOB_FLOW.EXTRA_BID:
                                      setShowExtraBid(true);
                                      break;
                                    default:
                                      break;
                                  }
                                }}
            />
          </div>
        </div>
      </main>
      </div>

      {!loading &&
        <Modal isOpen={showShare} dismissCallback={handleCloseModal}>
          <DefaultModal
            title={<Trans>T.PROS-PROFILE.Share {{sellerDisplayName}} profile</Trans>}
            closeButtonCallback={handleCloseModal}
            userMugshot={profile?.attributes.mugshot}
            showBackButton={false}
          >
            <SocialShare
              shareUrl={shareUrl}
              onActionCompleted={handleCloseModal} />
          </DefaultModal>
        </Modal>
      }

      <Modal isOpen={showExtraBid} autoWrapper dismissCallback={() => setShowExtraBid(false)}>
        <ExtraBidModal url={profile?.attributes.mugshot}
                       proId={profile?.id}
                       closeButtonCallback={() => setShowExtraBid(false)}
        />
      </Modal>

    </DefaultFullLayout>
  );
};

export default ProProfile;
